import * as Sentry from '@sentry/react';
import { isProduction } from '../../utils/isProduction';
import { ApolloError } from '@apollo/client';
import { ServerError } from '@apollo/client/link/utils/throwServerError';
import React, { ReactElement, RefObject, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router';
import { IFullUserDetails } from '../../graphql/schema/p4types';

export const SentryContext = React.createContext<{ lastEventId: RefObject<any> }>({
  lastEventId: {} as RefObject<any>,
});

export const SentryProvider = ({
  children,
  userDetails,
}: {
  children: ReactElement;
  userDetails: IFullUserDetails;
}) => {
  const lastEventId = useRef<string | undefined | null>();
  const history = useHistory();

  useEffect(() => {
    if (userDetails && !Sentry.isInitialized()) {
      const disableSentryMasking = userDetails.disableSentryMasking;
      const sentryEnabled = userDetails?.flags?.sentryEnabled ?? true;

      const replayOptions = {
        maskAllText: !disableSentryMasking,
        maskAllInputs: !disableSentryMasking,
        blockAllMedia: !disableSentryMasking,
        networkCaptureBodies: disableSentryMasking,
        networkDetailAllowUrls: disableSentryMasking ? ['/api/graphql'] : [],
      };

      Sentry.init({
        // eslint-disable-next-line no-process-env
        enabled: isProduction() && process.env.SENTRY_ENABLED !== 'false' && sentryEnabled,
        // eslint-disable-next-line no-process-env
        environment: process.env.NODE_ENV,
        // @ts-ignore
        release: `profit4@${__VERSION__}`,
        dsn: 'https://27f53833d16874fd33fda5a7a9b1c257@sentry.profit4.co.uk/2',
        integrations: [
          Sentry.reactRouterV5BrowserTracingIntegration({ history }),
          Sentry.replayIntegration(replayOptions),
        ],
        sampleRate: 0.5, // errors
        tracesSampleRate: 0.005, // traces
        replaysSessionSampleRate: 0, // replays
        replaysOnErrorSampleRate: 0.1, // error replays
        tracePropagationTargets: [/^\/api\//],
        beforeSend: (event, hint) => {
          const error = hint.originalException;
          const statusCode =
            error && error instanceof ApolloError && error.networkError
              ? (error.networkError as ServerError).statusCode
              : undefined;

          // not interested in reporting 4xx errors
          if (statusCode !== undefined && statusCode >= 400 && statusCode < 500) {
            return null;
          }

          if (error && event && lastEventId) {
            lastEventId.current = event.event_id;
          }

          return event;
        },
      });
    }
  }, [userDetails]);

  const sentryContext = useMemo(() => ({ lastEventId: lastEventId as RefObject<any> }), []);
  return <SentryContext.Provider value={sentryContext}>{children}</SentryContext.Provider>;
};
